import { useEffect, useMemo, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { useDeviceId } from '../hooks/useDeviceId.js'
import { useRoomData } from '../hooks/useRoomData.js'
import NowPlaying from './NowPlaying.jsx'
import Queue from './Queue.jsx'
import RequestList from './RequestList.jsx'
import RequestForm from './RequestForm.jsx'
import PlayedList from './PlayedList.jsx'
import RoomTabs from './RoomTabs.jsx'
import ClosedScreen from './ClosedScreen.jsx'

const ROOM_THEME = {
  main: { headerBorder: 'border-peak-accent/30',  pillBg: 'bg-peak-accent',  pillText: 'text-peak-accent',  label: 'Main Room' },
  late: { headerBorder: 'border-peak-accent2/30', pillBg: 'bg-peak-accent2', pillText: 'text-peak-accent2', label: 'Room 2' },
}

export default function DancerView() {
  const [params, setParams] = useSearchParams()
  const room = params.get('room') === 'late' ? 'late' : 'main'
  const setRoom = (r) => setParams({ room: r })
  const [showRequestForm, setShowRequestForm] = useState(false)
  // Default tab is "line" — most submissions are line dances, and the
  // app's preferred reading order is line on the LEFT, swing on the RIGHT.
  const [danceTypeTab, setDanceTypeTab] = useState('line') // line | swing
  const [statusTab, setStatusTab] = useState('pending')     // pending | queued | played

  const deviceId = useDeviceId()
  const { venue, rooms, room: roomConfig, requests, played, loading, error } = useRoomData(room)
  const theme = ROOM_THEME[room]

  // Room 2 is line-dance only — auto-flip the dance-type tab to "line"
  // whenever the dancer switches into Room 2. Swing tab is hidden there.
  useEffect(() => {
    if (room === 'late' && danceTypeTab !== 'line') {
      setDanceTypeTab('line')
    }
  }, [room, danceTypeTab])

  // IMPORTANT: every hook below must run on every render — do NOT add early
  // returns above this block, or you'll trip React's Rules of Hooks.

  // The dancer's request list shows pending, queued, AND played songs
  // together — all three states keep their position and vote count so
  // others can see the full picture of what was popular tonight. Only
  // pending requests are votable; queued/played show a static count.
  //
  // Cross-room sharing rule (narrow):
  //   • Same-room requests are always included, regardless of dance type
  //     or status. Pending and queued line dances stay per-room — a line
  //     dance submitted in Room 2 only shows up in Room 2's view, etc.
  //   • Only PLAYED line dances are pooled across rooms. This way a
  //     Room 2 dancer can see line dances that already played in Main
  //     Room (and vice versa) so the same song doesn't get requested or
  //     played twice. Pending/queued line dances in the other room are
  //     intentionally NOT surfaced.
  //   • Swing requests are strictly per-room in every state.
  const roomRequests = useMemo(
    () => requests.filter((r) => {
      const validStatus =
        r.status === 'pending' || r.status === 'queued' || r.status === 'played'
      if (!validStatus) return false
      if (r.room === room) return true
      // Cross-room share: ONLY played line dances. Pending/queued line
      // dances stay private to their original room.
      if (r.dance_type === 'line' && r.status === 'played') return true
      return false
    }),
    [requests, room],
  )

  // Counts per dance-type x status — used for the tab labels.
  const counts = useMemo(() => {
    const fresh = { swing: { pending: 0, queued: 0, played: 0 }, line: { pending: 0, queued: 0, played: 0 } }
    for (const r of roomRequests) {
      const dt = r.dance_type === 'line' ? 'line' : 'swing'
      const st = r.status === 'queued' ? 'queued' : r.status === 'played' ? 'played' : 'pending'
      fresh[dt][st]++
    }
    return fresh
  }, [roomRequests])

  // Visible list = filtered by dance type AND status, sorted alphabetically
  // for ALL statuses. Vote counts are instructor-only — sorting pending
  // by votes would leak the ranking via row order, so we use a neutral
  // alphabetical sort here regardless of state.
  const visibleRequests = useMemo(() => {
    const byTitleAsc = (a, b) =>
      (a.song_title || '').localeCompare(b.song_title || '', undefined, { sensitivity: 'base' })
    return roomRequests
      .filter((r) => r.dance_type === danceTypeTab && r.status === statusTab)
      .sort(byTitleAsc)
  }, [roomRequests, danceTypeTab, statusTab])

  // Per-room pending request count by this device — dance type does NOT
  // affect quota. Dancers get one shared bucket per room: any mix of
  // swing/line counts toward the same per-room cap. This closes the
  // abuse vector where a dancer could submit line dances under the
  // swing tag and have an instructor reclassify them, effectively
  // bypassing the line cap. Voting is still unlimited (one vote per
  // song per device).
  const myCount = (rm) => requests.filter(
    (r) => r.room === rm && r.device_id === deviceId && r.status === 'pending',
  ).length

  // Quota model:
  //   • Main Room: 7 total requests per dancer (any dance type).
  //   • Room 2:    2 total requests per dancer (forced line dance).
  // Instructor can override via Settings. Falls back to defaults if the
  // room config doesn't have the new field yet (legacy schema).
  const quotas = {
    main: { used: myCount('main'), max: rooms.main?.limits?.requests_per_user ?? 7 },
    late: { used: myCount('late'), max: rooms.late?.limits?.requests_per_user ?? 2 },
  }

  // A room is requestable if someone's claimed it, it's accepting
  // requests, AND the dancer is under their per-room cap.
  const mainAvailable =
    !!rooms.main?.active_instructor &&
    rooms.main?.accepting_requests !== false &&
    quotas.main.used < quotas.main.max

  const lateAvailable =
    !!rooms.late?.active_instructor &&
    rooms.late?.accepting_requests !== false &&
    quotas.late.used < quotas.late.max

  // The room is "unclaimed" from the dancer's perspective if the venue is
  // open but no instructor has claimed this room. Show a friendly screen
  // instead of an empty live UI.
  const isUnclaimed = venue.open && !roomConfig?.active_instructor

  // For Room 2: show a banner at the top of the dancer view distinguishing
  // practice mode (instructor warm-up) from line-dancing-active (the real
  // late-night line dance set). Both modes have the full UI with voting
  // and requests — only the banner copy differs.
  const lineDancingActive = roomConfig?.line_dancing_active === true
  const showRoom2Banner = room === 'late' && !!roomConfig?.active_instructor

  // Hard gates AFTER all hooks
  if (!loading && !venue.open) {
    return <ClosedScreenWithToggle room={room} setRoom={setRoom} mode="closed" />
  }
  if (!loading && isUnclaimed) {
    return <ClosedScreenWithToggle room={room} setRoom={setRoom} mode="unclaimed" />
  }

  return (
    <div className="min-h-full pb-32">
      <header className={`sticky top-0 z-10 bg-peak-bg/95 backdrop-blur border-b ${theme.headerBorder}`}>
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold tracking-tight">
              Peak <span className={`pill ${theme.pillBg} text-white ml-1 align-middle`}>{theme.label}</span>
            </h1>
            <p className="text-xs text-peak-muted">Song requests</p>
          </div>
          <Link to="/instructor" className="text-xs text-peak-muted hover:text-white">
            Instructor →
          </Link>
        </div>
        <div className="max-w-md mx-auto px-4 pb-3">
          <RoomTabs current={room} onChange={setRoom} />
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-4 space-y-4">
        {error && (
          <div className="card border-red-500/40 text-red-300">
            <p className="font-semibold">Couldn't reach the server</p>
            <p className="text-sm opacity-80">{error.message}</p>
          </div>
        )}

        {showRoom2Banner && (
          <Room2Banner active={lineDancingActive} />
        )}

        <NowPlaying nowPlaying={roomConfig?.now_playing} loading={loading} />

        <section>
          <h2 className="font-semibold mb-2">Coming up next</h2>
          <Queue queue={roomConfig?.queue} />
        </section>

        <section>
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="font-semibold">Requests</h2>
            <p className="text-xs text-peak-muted">
              {quotas[room].used}/{quotas[room].max} requests
            </p>
          </div>

          {/* Dance type tabs — Line dance / Swing.
              Order is line LEFT, swing RIGHT (matches request form +
              instructor desktop layout). Hidden for Room 2 since it's
              line-only; danceTypeTab is forced to "line" via the
              useEffect above. */}
          {room !== 'late' && (
            <div className="flex bg-peak-surface rounded-xl p-1 text-sm font-medium mb-2">
              {[
                { id: 'line',  label: 'Line dance' },
                { id: 'swing', label: 'Swing' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setDanceTypeTab(tab.id)}
                  className={`flex-1 py-2 rounded-lg transition-colors ${
                    danceTypeTab === tab.id
                      ? `${theme.pillBg} text-white shadow`
                      : 'text-peak-muted hover:text-white'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* Status filter — To be decided / Queued / Played */}
          <div className="flex gap-1 bg-peak-bg rounded-lg p-0.5 text-xs font-medium mb-3">
            {[
              { id: 'pending', label: 'To be decided' },
              { id: 'queued',  label: 'Queued' },
              { id: 'played',  label: 'Played' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setStatusTab(tab.id)}
                className={`flex-1 py-1.5 rounded transition-colors ${
                  statusTab === tab.id
                    ? 'bg-white/10 text-white'
                    : 'text-peak-muted hover:text-white'
                }`}
              >
                {tab.label} ({counts[danceTypeTab][tab.id]})
              </button>
            ))}
          </div>

          <RequestList
            requests={visibleRequests}
            deviceId={deviceId}
          />
          {visibleRequests.length === 0 && !loading && (
            <p className="card text-peak-muted text-sm text-center">
              {emptyStateCopy(danceTypeTab, statusTab)}
            </p>
          )}
        </section>

        <section>
          <h2 className="font-semibold mb-2">Already played</h2>
          {/*
            Defensive filter: only show plays from THIS night. Even if old
            entries somehow leaked in (edge cases with backfill timing or
            partial wipes), the cutoff at venue.opened_at keeps the dancer
            view strictly tonight-only.
          */}
          <PlayedList
            played={played.filter(
              (p) =>
                p.room === room &&
                (!venue.opened_at || (p.played_at || 0) >= venue.opened_at)
            )}
          />
        </section>

        {/* Floating action button — enabled if at least one room is requestable. */}
        <button
          className={`${theme.pillBg} text-white font-semibold px-6 py-3 rounded-xl shadow-2xl fixed bottom-5 left-1/2 -translate-x-1/2 active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed`}
          onClick={() => setShowRequestForm(true)}
          disabled={!mainAvailable && !lateAvailable}
        >
          {!mainAvailable && !lateAvailable
            ? 'Requests closed'
            : `+ Request a song`}
        </button>

        {showRequestForm && (
          <RequestForm
            defaultRoom={room}
            deviceId={deviceId}
            mainAvailable={mainAvailable}
            lateAvailable={lateAvailable}
            mainQuotas={quotas.main}
            lateQuotas={quotas.late}
            existingRequests={requests}
            onClose={() => setShowRequestForm(false)}
          />
        )}
      </main>
    </div>
  )
}

function emptyStateCopy(danceType, status) {
  const dt = danceType === 'swing' ? 'swing' : 'line dance'
  if (status === 'pending') return `No ${dt} requests waiting yet. Be the first!`
  if (status === 'queued')  return `No ${dt} songs queued yet.`
  if (status === 'played')  return `No ${dt} songs played yet tonight.`
  return ''
}

/** ClosedScreen wrapper that adds a tiny room-toggle so dancers can flip rooms. */
function ClosedScreenWithToggle({ room, setRoom, mode }) {
  return (
    <div className="min-h-full">
      <div className="max-w-md mx-auto px-4 pt-4">
        <RoomTabs current={room} onChange={setRoom} />
      </div>
      <ClosedScreen room={room} mode={mode} />
    </div>
  )
}

/**
 * Banner shown on the dancer's Room 2 view. Two states:
 *   • active=false → "Practice mode" + description (warm-up swing music
 *     before line dancing kicks off; instructor activates when ready).
 *   • active=true  → "Line Dancing Only" with no description; instructor
 *     has activated the line-dance set.
 *
 * Uses the Room 2 purple accent so it visually anchors with the rest of
 * the room's theme.
 */
function Room2Banner({ active }) {
  return (
    <div
      className={`card border-2 ${
        active ? 'border-peak-accent2 bg-peak-accent2/10' : 'border-peak-accent2/40 bg-peak-accent2/5'
      }`}
    >
      <p className="font-bold text-peak-accent2">
        {active ? 'Line Dancing Only' : 'Practice mode — line dance opens at 11:30pm'}
      </p>
      {!active && (
        <p className="text-xs text-peak-muted mt-1">
          Swing music is playing for warm-up. You can submit line dance requests and vote
          right now, but they won't be queued or played until practice mode ends and the
          line dance set kicks off at 11:30pm.
        </p>
      )}
    </div>
  )
}
